import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ArrowDropDown } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { useMeasures } from "hooks";
import { useDealActions, useMeasuresSelector, useReportsMetaSelector, useAccountSelector } from "store";
import type { CardConfig, ChartCardConfig, ChartType } from "store/types";
import { normalizeGroupByCharacteristic } from "utils";

import { SectionLabel } from "./SectionLabel";

const CHART_TYPES: Array<{ value: ChartType; label: string }> = [
  { value: "pie", label: "Pie" },
  { value: "bar", label: "Bar" },
  { value: "column", label: "Column" },
  { value: "barStacked", label: "Stacked Bar" },
  { value: "columnStacked", label: "Stacked Column" },
  { value: "waterfall", label: "Waterfall" },
  { value: "top5", label: "Top 5" },
  { value: "scatter", label: "X/Y Scatter" },
  { value: "clusterBar", label: "Cluster Bar" }
];

interface Props {
  config: ChartCardConfig;
  onUpdateConfig: (c: CardConfig) => void;
}

export const ChartSettings: React.FC<Props> = ({ config, onUpdateConfig }) => {
  const { getMeasuresAction } = useDealActions();
  const measures = useMeasuresSelector();
  const { characteristics } = useReportsMetaSelector();
  const { support_investor_groups } = useAccountSelector() || {};

  const [loadingMeasures, setLoadingMeasures] = useState(false);

  useEffect(() => {
    if (measures.length === 0) {
      setLoadingMeasures(true);
      getMeasuresAction().finally(() => setLoadingMeasures(false));
    }
  }, [measures.length, getMeasuresAction]);

  const measureOptions = useMemo(() => measures.map((m) => ({ value: m.name, label: m.name })), [measures]);

  const groupedValues = useMeasures(false);
  const axisOptions = useMemo(() => {
    return groupedValues
      .filter((v) => typeof v === "string" || (typeof v === "object" && (v as any).role !== "category"))
      .map((v) => {
        if (typeof v === "string") return { value: v, label: v };
        return { value: (v as any).value || (v as any).text, label: (v as any).text || (v as any).value };
      });
  }, [groupedValues]);

  const toAxis = (value: string) => {
    const isCalc = value.startsWith("calc-field-");
    return { type: isCalc ? "calc" : "measure", name: isCalc ? value.replace("calc-field-", "") : value };
  };

  const groupByOptions = useMemo(
    () => [
      { text: "Investor", value: "investor" },
      ...(support_investor_groups ? [{ text: "Investor Group", value: "investorGroup" }] : []),
      { text: "Deal", value: "deal" },
      ...characteristics.map((char) =>
        typeof char === "string" ? { text: char, value: normalizeGroupByCharacteristic(char) } : char
      )
    ],
    [characteristics, support_investor_groups]
  );

  const currentChart = useMemo(() => {
    if (!config.graphDataFormation)
      return {
        chartType: config.chartType,
        measure: "",
        groupBy: "",
        xAxis: undefined as { name: string; type: string } | undefined,
        yAxis: undefined as { name: string; type: string } | undefined
      };
    const key = Object.keys(config.graphDataFormation)[0];
    const entry = config.graphDataFormation[key];
    return {
      chartType: entry?.chartType || config.chartType,
      measure: entry?.measure || "",
      groupBy: entry?.groupBy || "",
      xAxis: entry?.xAxis,
      yAxis: entry?.yAxis
    };
  }, [config]);

  const updateChart = useCallback(
    (updates: {
      chartType?: ChartType;
      measure?: string;
      groupBy?: string;
      xAxis?: { name: string; type: string };
      yAxis?: { name: string; type: string };
    }) => {
      const next = { ...currentChart, ...updates };
      const graphDataFormation = {
        chart1: {
          measure: next.measure || "",
          groupBy: next.groupBy || "",
          chartType: next.chartType,
          ...(next.chartType === "scatter" && next.xAxis && { xAxis: next.xAxis }),
          ...(next.chartType === "scatter" && next.yAxis && { yAxis: next.yAxis })
        }
      };
      onUpdateConfig({ ...config, chartType: next.chartType, graphDataFormation });
    },
    [config, currentChart, onUpdateConfig]
  );

  return (
    <>
      <Box>
        <SectionLabel>Chart Type</SectionLabel>
        <FormControl fullWidth size="small">
          <Select
            value={currentChart.chartType}
            onChange={(e) => updateChart({ chartType: e.target.value as ChartType })}
            IconComponent={(props) => <ArrowDropDown {...props} sx={{ fontSize: 24, color: "#5d7782" }} />}
            sx={{
              fontSize: 12,
              "& .MuiSelect-select": {
                padding: "8px 32px 6px 14px"
              },
              "& .MuiSelect-icon": { right: 10, color: "#6B7280" }
            }}
          >
            {CHART_TYPES.map((ct) => (
              <MenuItem key={ct.value} value={ct.value} sx={{ fontSize: 12 }}>
                {ct.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      {currentChart.chartType === "scatter" ? (
        <>
          <Box>
            <SectionLabel>X Axis</SectionLabel>
            <Autocomplete
              size="small"
              options={axisOptions}
              value={axisOptions.find((o) => o.value === currentChart.xAxis?.name) || null}
              onChange={(_, v) => updateChart({ xAxis: v ? toAxis(v.value) : undefined })}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              loading={loadingMeasures}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select X axis"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingMeasures ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }
                  }}
                  sx={{ "& .MuiInputBase-input": { fontSize: 12 } }}
                />
              )}
            />
          </Box>
          <Box>
            <SectionLabel>Y Axis</SectionLabel>
            <Autocomplete
              size="small"
              options={axisOptions}
              value={axisOptions.find((o) => o.value === currentChart.yAxis?.name) || null}
              onChange={(_, v) => updateChart({ yAxis: v ? toAxis(v.value) : undefined })}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              loading={loadingMeasures}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select Y axis"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingMeasures ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }
                  }}
                  sx={{ "& .MuiInputBase-input": { fontSize: 12 } }}
                />
              )}
            />
          </Box>
          <Box>
            <SectionLabel>Group By</SectionLabel>
            <Autocomplete
              size="small"
              options={groupByOptions}
              value={groupByOptions.find((o) => o.value === currentChart.groupBy) || null}
              onChange={(_, v) => updateChart({ groupBy: v?.value || "" })}
              getOptionLabel={(o) => o.text}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select grouping"
                  sx={{ "& .MuiInputBase-input": { fontSize: 12 } }}
                />
              )}
            />
          </Box>
        </>
      ) : (
        <>
          <Box>
            <SectionLabel>Value</SectionLabel>
            <Autocomplete
              size="small"
              options={measureOptions}
              value={measureOptions.find((o) => o.value === currentChart.measure) || null}
              onChange={(_, v) => updateChart({ measure: v?.value || "" })}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              loading={loadingMeasures}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select measure"
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingMeasures ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }
                  }}
                  sx={{ "& .MuiInputBase-input": { fontSize: 12 } }}
                />
              )}
            />
          </Box>
          <Box>
            <SectionLabel>Group By</SectionLabel>
            <Autocomplete
              size="small"
              options={groupByOptions}
              value={groupByOptions.find((o) => o.value === currentChart.groupBy) || null}
              onChange={(_, v) => updateChart({ groupBy: v?.value || "" })}
              getOptionLabel={(o) => o.text}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select grouping"
                  sx={{ "& .MuiInputBase-input": { fontSize: 12 } }}
                />
              )}
            />
          </Box>
        </>
      )}
    </>
  );
};
